// PWA Service Worker Registration and Update Management
import { useState, useEffect } from 'react';

interface PWAUpdateState {
  needRefresh: boolean;
  updateApp: () => void;
  closeRefreshPrompt: () => void;
}

export function usePWA(): PWAUpdateState {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Listen for controller change (new SW taking over) and reload automatically
    const handleControllerChange = () => {
      console.log('🔄 New Service Worker activated - reloading page automatically...');
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Get existing registration
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) {
        setRegistration(reg);

        // Check for updates on registration
        const checkForUpdates = () => {
          reg.update().catch((err) => {
            console.log('SW update check failed:', err);
          });
        };

        // Check for updates on page visibility change (when user comes back to tab)
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            console.log('👀 Page visible - checking for updates...');
            checkForUpdates();
          }
        };

        // Check for updates on focus (when user returns to app)
        const handleFocus = () => {
          console.log('🔍 Window focused - checking for updates...');
          checkForUpdates();
        };

        // Listen for new service worker (this happens when new SW is found)
        const onUpdateFound = () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          console.log('🆕 New Service Worker detected - installing...');

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('✅ New Service Worker installed - will activate and reload automatically');
              // No need to show prompt - SW will activate automatically and controllerchange will reload
            }
          });
        };

        reg.addEventListener('updatefound', onUpdateFound);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Check immediately
        checkForUpdates();

        return () => {
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
          reg.removeEventListener('updatefound', onUpdateFound);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('focus', handleFocus);
        };
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  const updateApp = () => {
    // Manual update - just reload
    console.log('🔄 Manual reload requested');
    window.location.reload();
  };

  const closeRefreshPrompt = () => {
    setNeedRefresh(false);
  };

  return {
    needRefresh,
    updateApp,
    closeRefreshPrompt,
  };
}

// Check if app is installed (running as PWA)
export function isPWAInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true ||
         document.referrer.includes('android-app://');
}

// Detect platform
export function getPlatform(): 'ios' | 'android' | 'desktop' | 'macos' {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }
  
  if (/android/.test(userAgent)) {
    return 'android';
  }
  
  // Check for macOS Safari specifically
  if (/macintosh|mac os x/.test(userAgent)) {
    return 'macos';
  }
  
  return 'desktop';
}

// Check if browser supports PWA installation
export function canInstallPWA(): boolean {
  // iOS Safari
  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  const isSafari = /safari/.test(navigator.userAgent.toLowerCase()) && 
                   !/chrome|crios|fxios/.test(navigator.userAgent.toLowerCase());
  
  if (isIOS && isSafari) {
    return !isPWAInstalled();
  }
  
  // Android/Desktop - check for beforeinstallprompt event
  return 'BeforeInstallPromptEvent' in window || !isPWAInstalled();
}
